import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageShell, { SectionTitle } from './PageShell';
import { api } from '../lib/api';
import { AlertTriangle, Send, CheckCircle2, Loader2, ArrowLeft } from 'lucide-react';
import { celebrate } from '../lib/celebrate';

export default function SignalerErreurPage() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    page_url: '',
    faulty_text: '',
    suggestion: '',
    reporter_name: '',
    reporter_email: '',
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  // Pré-remplir l'URL depuis le paramètre ?page= ou depuis document.referrer
  useEffect(() => {
    const p = params.get('page');
    if (p) {
      setForm((f) => ({ ...f, page_url: p }));
    } else if (typeof document !== 'undefined' && document.referrer && document.referrer.includes(window.location.host)) {
      setForm((f) => ({ ...f, page_url: document.referrer }));
    }
  }, [params]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.faulty_text.trim() || form.faulty_text.trim().length < 5) {
      setError('Merci d\'indiquer le texte fautif (au moins 5 caractères).');
      return;
    }
    setSending(true);
    try {
      await api.post('/reports', form);
      setDone(true);
      celebrate();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erreur lors de l\'envoi. Réessaie dans un instant.');
    } finally {
      setSending(false);
    }
  };

  return (
    <PageShell>
      <section className="max-w-3xl mx-auto px-4 lg:px-6 mt-8 sm:mt-12">
        <SectionTitle
          kicker="Amélioration continue"
          title="Signaler une erreur"
          sub="Coquille, chiffre erroné, article de loi obsolète, mauvais panneau... Aide-nous à corriger. Chaque signalement est examiné sous 72 h par un ECSR."
        />

        {done ? (
          <div
            data-testid="report-success"
            className="rounded-2xl border border-emerald-700/40 bg-emerald-950/20 p-6 sm:p-8 text-center"
          >
            <CheckCircle2 className="mx-auto text-emerald-400 mb-3" size={40} />
            <h3 className="font-serif-editorial text-white text-[22px] sm:text-[28px]">Merci pour ton signalement !</h3>
            <p className="text-white/70 text-[13.5px] sm:text-[15px] mt-3 max-w-xl mx-auto leading-relaxed">
              Bruno et l'équipe ECSR examinent chaque remontée sous 72 h ouvrées. Si tu as laissé ton email, tu recevras une réponse après vérification.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Link
                to="/"
                data-testid="report-back-home"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13.5px] font-semibold"
              >
                <ArrowLeft size={15} /> Retour à l'accueil
              </Link>
              <button
                onClick={() => { setDone(false); setForm({ page_url: '', faulty_text: '', suggestion: '', reporter_name: '', reporter_email: '' }); }}
                data-testid="report-another"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/15 text-white/80 hover:text-white text-[13.5px] font-semibold"
              >
                Signaler un autre
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7 lg:p-9 space-y-5" data-testid="report-form">
            <div className="flex items-start gap-3 rounded-xl border border-red-700/25 bg-red-950/10 p-3 sm:p-4">
              <AlertTriangle className="text-red-400 shrink-0 mt-0.5" size={18} />
              <p className="text-white/75 text-[13px] sm:text-[13.5px] leading-relaxed">
                Sois précis(e) : cite le passage exact, indique l'URL de la page concernée, et si possible propose la correction correcte. Merci ! 🙏
              </p>
            </div>

            <div>
              <label className="text-white/70 text-[12px] uppercase tracking-wider font-mono-spec block mb-2">
                URL de la page concernée <span className="text-white/40 normal-case">(optionnel — auto-détectée si tu viens d'y être)</span>
              </label>
              <input
                type="text"
                value={form.page_url}
                onChange={set('page_url')}
                placeholder="https://codevoiture.fr/eleve  ou  /demos-interactives"
                data-testid="report-page-url"
                className="w-full h-11 px-4 rounded-xl bg-[#141416] border border-white/10 text-white text-[13.5px] outline-none focus:border-red-500 transition"
              />
            </div>

            <div>
              <label className="text-white/70 text-[12px] uppercase tracking-wider font-mono-spec block mb-2">
                Texte fautif <span className="text-red-400 normal-case">*</span>
              </label>
              <textarea
                required
                value={form.faulty_text}
                onChange={set('faulty_text')}
                placeholder="Copie/colle ici le texte exact qui est erroné, ou décris le problème (ex : « BEA : 78 h en boîte manuelle » — cela n'existe pas)."
                rows={5}
                data-testid="report-faulty-text"
                className="w-full px-4 py-3 rounded-xl bg-[#141416] border border-white/10 text-white text-[13.5px] outline-none focus:border-red-500 transition resize-y leading-relaxed"
              />
            </div>

            <div>
              <label className="text-white/70 text-[12px] uppercase tracking-wider font-mono-spec block mb-2">
                Ta suggestion / correction <span className="text-white/40 normal-case">(optionnel mais très utile)</span>
              </label>
              <textarea
                value={form.suggestion}
                onChange={set('suggestion')}
                placeholder="Ex : « Il faut plutôt écrire : passerelle boîte méca + 7 h de formation manuelle. »"
                rows={3}
                data-testid="report-suggestion"
                className="w-full px-4 py-3 rounded-xl bg-[#141416] border border-white/10 text-white text-[13.5px] outline-none focus:border-red-500 transition resize-y leading-relaxed"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-white/70 text-[12px] uppercase tracking-wider font-mono-spec block mb-2">
                  Ton prénom <span className="text-white/40 normal-case">(optionnel)</span>
                </label>
                <input
                  type="text"
                  value={form.reporter_name}
                  onChange={set('reporter_name')}
                  data-testid="report-name"
                  className="w-full h-11 px-4 rounded-xl bg-[#141416] border border-white/10 text-white text-[13.5px] outline-none focus:border-red-500 transition"
                />
              </div>
              <div>
                <label className="text-white/70 text-[12px] uppercase tracking-wider font-mono-spec block mb-2">
                  Ton email <span className="text-white/40 normal-case">(pour être recontacté(e))</span>
                </label>
                <input
                  type="email"
                  value={form.reporter_email}
                  onChange={set('reporter_email')}
                  data-testid="report-email"
                  className="w-full h-11 px-4 rounded-xl bg-[#141416] border border-white/10 text-white text-[13.5px] outline-none focus:border-red-500 transition"
                />
              </div>
            </div>

            {error && (
              <div className="rounded-xl border border-red-700/40 bg-red-950/25 px-4 py-3 text-red-300 text-[13px]" data-testid="report-error">
                {error}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={sending}
                data-testid="report-submit"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-[14px] font-semibold shadow-lg shadow-red-900/40"
              >
                {sending ? <><Loader2 className="animate-spin" size={16} /> Envoi…</> : <><Send size={15} /> Envoyer mon signalement</>}
              </button>
              <p className="text-white/45 text-[12px] font-mono-spec">
                72 h ouvrées · aucune revente de données · sans inscription
              </p>
            </div>
          </form>
        )}
      </section>
    </PageShell>
  );
}
